/** Pure Bundle / release-control display mapping — no I/O. D4F. */

import {
  PORTAL_BUNDLE_RC16_FILE_COUNT,
  PORTAL_BUNDLE_RC16_RELEASE_ID,
  PORTAL_BUNDLE_RC16_SHA256,
  PORTAL_BUNDLE_RC17_FILE_COUNT,
  PORTAL_BUNDLE_RC17_RELEASE_ID,
  PORTAL_BUNDLE_RC17_SHA256,
  PORTAL_BUNDLE_RC18_FILE_COUNT,
  PORTAL_BUNDLE_RC18_RELEASE_ID,
  PORTAL_BUNDLE_RC18_SHA256,
  PORTAL_BUNDLE_RC19_FILE_COUNT,
  PORTAL_BUNDLE_RC19_RELEASE_ID,
  PORTAL_BUNDLE_RC19_SHA256,
  PORTAL_BUNDLE_RC20_FILE_COUNT,
  PORTAL_BUNDLE_RC20_RELEASE_ID,
  PORTAL_BUNDLE_RC20_SHA256,
  PORTAL_BUNDLE_RC21_FILE_COUNT,
  PORTAL_BUNDLE_RC21_RELEASE_ID,
  PORTAL_BUNDLE_RC21_SHA256,
  PORTAL_BUNDLE_RC22_FILE_COUNT,
  PORTAL_BUNDLE_RC22_RELEASE_ID,
  PORTAL_BUNDLE_RC22_SHA256,
  PORTAL_BUNDLE_RC23_FILE_COUNT,
  PORTAL_BUNDLE_RC23_RELEASE_ID,
  PORTAL_BUNDLE_RC23_SHA256,
  PORTAL_BUNDLE_RC24_FILE_COUNT,
  PORTAL_BUNDLE_RC24_RELEASE_ID,
  PORTAL_BUNDLE_RC24_SHA256,
  PORTAL_BUNDLE_RC25_FILE_COUNT,
  PORTAL_BUNDLE_RC25_RELEASE_ID,
  PORTAL_BUNDLE_RC25_SHA256,
  PORTAL_BUNDLE_RC26_FILE_COUNT,
  PORTAL_BUNDLE_RC26_RELEASE_ID,
  PORTAL_BUNDLE_RC26_SHA256,
} from "@vital/shared";
import type { StatusTone } from "./overview-ops";

export type BundleTemplateRow = {
  id: string;
  releaseId: string;
  sourceFilename: string;
  sha256: string;
  fileCount: number;
  status: string;
  isDefault: boolean;
  verifiedAt: string | null;
  notes: string | null;
  createdAt: string;
};

export type BundleJobRow = {
  id: string;
  sequence: number;
  siteId: string;
  siteCode: string;
  templateId: string;
  templateReleaseId: string;
  templateSha256: string;
  status: string;
  requestedAt: string;
  completedAt: string | null;
  failureCode: string | null;
  expiresAt: string | null;
  artifactSha256: string | null;
  artifactFilename: string | null;
  artifactFileCount: number | null;
  artifactByteSize: number | null;
  artifactId: string | null;
};

/** Flatten nested API job → inventory row (presentation only). */
export function toBundleJobRow(job: {
  id: string;
  sequence: number;
  siteId: string;
  siteCode: string;
  templateId: string;
  templateReleaseId: string;
  templateSha256: string;
  status: string;
  requestedAt: string;
  completedAt: string | null;
  failureCode: string | null;
  expiresAt: string | null;
  artifact: {
    id: string;
    generatedFilename: string;
    artifactSha256: string;
    fileCount: number;
    byteSize: number;
  } | null;
}): BundleJobRow {
  return {
    id: job.id,
    sequence: job.sequence,
    siteId: job.siteId,
    siteCode: job.siteCode,
    templateId: job.templateId,
    templateReleaseId: job.templateReleaseId,
    templateSha256: job.templateSha256,
    status: job.status,
    requestedAt: job.requestedAt,
    completedAt: job.completedAt,
    failureCode: job.failureCode,
    expiresAt: job.expiresAt,
    artifactSha256: job.artifact?.artifactSha256 ?? null,
    artifactFilename: job.artifact?.generatedFilename ?? null,
    artifactFileCount: job.artifact?.fileCount ?? null,
    artifactByteSize: job.artifact?.byteSize ?? null,
    artifactId: job.artifact?.id ?? null,
  };
}

/** Site-scoped ZIP only — no fleet ring on this API row. */
export function jobScopeLabel(siteCode: string): string {
  return `سایت ${siteCode} — بدون حلقه ناوگان در این رکورد`;
}

/**
 * Bundle jobs have no content-snapshot rollback field.
 * Content rollback lives on تاریخچه انتشار; Fleet holds deployment intent.
 */
export function jobRollbackRelationship(): StatusDisplay {
  return {
    label: "بدون بازگردانی Bundle",
    tone: "neutral",
    detail:
      "این سطح فقط ZIP سایت است. بازگردانی محتوای پورتال در «تاریخچه انتشار»؛ استقرار دستی در ناوگان — بدون تأیید خودکار RouterOS",
  };
}

export type TemplateSortKey = "release" | "status" | "created";
export type JobSortKey = "requested" | "site" | "status" | "sequence";

export type StatusDisplay = { label: string; tone: StatusTone; detail?: string };

/** Frozen RC constants used for recognition only — never rewrite hashes. */
export const FROZEN_RC_AUTHORITY: ReadonlyArray<{
  releaseId: string;
  sha256: string;
  fileCount: number;
  label: string;
}> = [
  {
    releaseId: PORTAL_BUNDLE_RC16_RELEASE_ID,
    sha256: PORTAL_BUNDLE_RC16_SHA256,
    fileCount: PORTAL_BUNDLE_RC16_FILE_COUNT,
    label: "rc.16",
  },
  {
    releaseId: PORTAL_BUNDLE_RC17_RELEASE_ID,
    sha256: PORTAL_BUNDLE_RC17_SHA256,
    fileCount: PORTAL_BUNDLE_RC17_FILE_COUNT,
    label: "rc.17",
  },
  {
    releaseId: PORTAL_BUNDLE_RC18_RELEASE_ID,
    sha256: PORTAL_BUNDLE_RC18_SHA256,
    fileCount: PORTAL_BUNDLE_RC18_FILE_COUNT,
    label: "rc.18",
  },
  {
    releaseId: PORTAL_BUNDLE_RC19_RELEASE_ID,
    sha256: PORTAL_BUNDLE_RC19_SHA256,
    fileCount: PORTAL_BUNDLE_RC19_FILE_COUNT,
    label: "rc.19",
  },
  {
    releaseId: PORTAL_BUNDLE_RC20_RELEASE_ID,
    sha256: PORTAL_BUNDLE_RC20_SHA256,
    fileCount: PORTAL_BUNDLE_RC20_FILE_COUNT,
    label: "rc.20",
  },
  {
    releaseId: PORTAL_BUNDLE_RC21_RELEASE_ID,
    sha256: PORTAL_BUNDLE_RC21_SHA256,
    fileCount: PORTAL_BUNDLE_RC21_FILE_COUNT,
    label: "rc.21",
  },
  {
    releaseId: PORTAL_BUNDLE_RC22_RELEASE_ID,
    sha256: PORTAL_BUNDLE_RC22_SHA256,
    fileCount: PORTAL_BUNDLE_RC22_FILE_COUNT,
    label: "rc.22",
  },
  {
    releaseId: PORTAL_BUNDLE_RC23_RELEASE_ID,
    sha256: PORTAL_BUNDLE_RC23_SHA256,
    fileCount: PORTAL_BUNDLE_RC23_FILE_COUNT,
    label: "rc.23",
  },
  {
    releaseId: PORTAL_BUNDLE_RC24_RELEASE_ID,
    sha256: PORTAL_BUNDLE_RC24_SHA256,
    fileCount: PORTAL_BUNDLE_RC24_FILE_COUNT,
    label: "rc.24",
  },
  {
    releaseId: PORTAL_BUNDLE_RC25_RELEASE_ID,
    sha256: PORTAL_BUNDLE_RC25_SHA256,
    fileCount: PORTAL_BUNDLE_RC25_FILE_COUNT,
    label: "rc.25",
  },
  {
    releaseId: PORTAL_BUNDLE_RC26_RELEASE_ID,
    sha256: PORTAL_BUNDLE_RC26_SHA256,
    fileCount: PORTAL_BUNDLE_RC26_FILE_COUNT,
    label: "rc.26",
  },
];

/** Read release ID from a frozen hotspot ZIP name. */
export function releaseIdFromBundleFilename(filename: string): string | null {
  const base = filename.replace(/^.*[/\\]/, "");
  const match = /^vital-hotspot-(0\.0\.0-rc\.\d+)\.zip$/i.exec(base);
  return match?.[1] ?? null;
}

/**
 * Owner-declared verify fields.
 * Use frozen SHA-256 for the imported release. Do not copy the server hash.
 */
export function verifyDeclarationDefaults(
  template: Pick<BundleTemplateRow, "releaseId" | "fileCount">,
): { releaseId: string; sha256: string; fileCount: number } {
  const frozen = FROZEN_RC_AUTHORITY.find((row) => row.releaseId === template.releaseId);
  if (frozen) {
    return {
      releaseId: frozen.releaseId,
      sha256: frozen.sha256,
      fileCount: frozen.fileCount,
    };
  }
  return { releaseId: template.releaseId, sha256: "", fileCount: template.fileCount };
}

export function frozenAuthorityForRelease(releaseId: string) {
  return FROZEN_RC_AUTHORITY.find((row) => row.releaseId === releaseId) ?? null;
}

/** True when the imported release ID does not match the frozen hash of the ZIP. */
export function templateReleaseHashConflict(
  template: Pick<BundleTemplateRow, "releaseId" | "sha256">,
): boolean {
  const byRelease = frozenAuthorityForRelease(template.releaseId);
  if (!byRelease) return false;
  return byRelease.sha256 !== template.sha256.toLowerCase();
}

export function truncateSha256(value: string, head = 12): string {
  if (value.length <= head + 1) return value;
  return `${value.slice(0, head)}…`;
}

export function matchFrozenRelease(
  releaseId: string,
  sha256?: string | null,
): { label: string; sha256: string; shaMatches: boolean | null } | null {
  const hit = FROZEN_RC_AUTHORITY.find((r) => r.releaseId === releaseId);
  if (!hit) return null;
  if (!sha256) return { label: hit.label, sha256: hit.sha256, shaMatches: null };
  return {
    label: hit.label,
    sha256: hit.sha256,
    shaMatches: sha256.toLowerCase() === hit.sha256.toLowerCase(),
  };
}

export function templateStatusDisplay(status: string): StatusDisplay {
  if (status === "verified") return { label: "تأییدشده", tone: "success" };
  if (status === "draft") return { label: "پیش‌نویس", tone: "info" };
  if (status === "retired") return { label: "بازنشسته", tone: "neutral" };
  return { label: status, tone: "neutral" };
}

export function templateFrozenBadge(
  releaseId: string,
  sha256: string,
): StatusDisplay | null {
  const frozen = matchFrozenRelease(releaseId, sha256);
  if (!frozen) return null;
  if (frozen.shaMatches === false) {
    return {
      label: `منجمد ${frozen.label} — SHA ناهم‌خوان`,
      tone: "danger",
      detail: "شناسه نسخه با هش مرجع منجمد هم‌خوان نیست — بدون بازنویسی هش",
    };
  }
  return {
    label: `منجمد ${frozen.label}`,
    tone: "info",
    detail: "الگوی مرجع منجمد — CMS چیزی به RouterOS آپلود نمی‌کند؛ تولید انبوه production deferred/unexecuted",
  };
}

export function jobStatusDisplay(status: string): StatusDisplay {
  switch (status) {
    case "queued":
      return { label: "در صف", tone: "info" };
    case "generating":
      return { label: "در حال ساخت", tone: "info" };
    case "ready":
      return {
        label: "آمادهٔ دانلود",
        tone: "success",
        detail: "دانلود دستی — نه استقرار خودکار RouterOS و نه تأیید rollout تولید",
      };
    case "failed":
      return { label: "ناموفق", tone: "danger" };
    case "expired":
      return { label: "منقضی", tone: "neutral" };
    case "revoked":
      return { label: "ابطال‌شده", tone: "neutral" };
    default:
      return { label: status, tone: "neutral" };
  }
}

export function matchesTemplateQuery(row: BundleTemplateRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [row.releaseId, row.sha256, row.sourceFilename, row.status, row.notes ?? ""]
    .join(" ")
    .toLowerCase()
    .includes(q);
}

export function matchesTemplateStatus(row: BundleTemplateRow, status: string): boolean {
  if (status === "all") return true;
  if (status === "frozen") return matchFrozenRelease(row.releaseId, row.sha256) != null;
  return row.status === status;
}

export function filterAndSortTemplates(
  rows: BundleTemplateRow[],
  opts: { query: string; status: string; sort: TemplateSortKey },
): BundleTemplateRow[] {
  const filtered = rows.filter(
    (r) => matchesTemplateQuery(r, opts.query) && matchesTemplateStatus(r, opts.status),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "status") return a.status.localeCompare(b.status);
    if (opts.sort === "created") return b.createdAt.localeCompare(a.createdAt);
    return a.releaseId.localeCompare(b.releaseId, "en");
  });
  return sorted;
}

export function matchesJobQuery(row: BundleJobRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    row.siteCode,
    row.templateReleaseId,
    row.templateSha256,
    row.artifactSha256 ?? "",
    row.status,
    String(row.sequence),
    row.failureCode ?? "",
  ]
    .join(" ")
    .toLowerCase()
    .includes(q);
}

export function matchesJobStatus(row: BundleJobRow, status: string): boolean {
  if (status === "all") return true;
  return row.status === status;
}

export function filterAndSortJobs(
  rows: BundleJobRow[],
  opts: { query: string; status: string; sort: JobSortKey },
): BundleJobRow[] {
  const filtered = rows.filter(
    (r) => matchesJobQuery(r, opts.query) && matchesJobStatus(r, opts.status),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "site") return a.siteCode.localeCompare(b.siteCode, "en");
    if (opts.sort === "status") return a.status.localeCompare(b.status);
    if (opts.sort === "sequence") return b.sequence - a.sequence;
    return b.requestedAt.localeCompare(a.requestedAt);
  });
  return sorted;
}

export type RelatedBundleLink = { id: string; label: string; to: string };

export function relatedBundleLinks(): RelatedBundleLink[] {
  return [
    { id: "sites", label: "سایت‌ها", to: "/sites" },
    { id: "fleet", label: "ناوگان و استقرار", to: "/fleet" },
    { id: "versions", label: "تاریخچه انتشار", to: "/versions" },
    { id: "audit", label: "سابقه تغییرات", to: "/audit" },
  ];
}

/** Honest ops note — presentation only. */
export const BUNDLE_AUTHORITY_NOTE =
  "Bundle فقط ZIP دانلود می‌سازد. استقرار RouterOS خودکار نیست؛ rollout تولید rc.21 deferred/unexecuted است.";
